<template>
  <div class="profile-page">
    <div class="main-wrapper">
      <div class="content main_content">
        <side-menu></side-menu>

        <div class="chat overflow-y-auto">
          <div class="profile-bg-parallax"></div>

          <div v-if="loading" class="d-flex justify-content-center align-items-center h-full">
            <div class="loading-spinner"></div>
          </div>

          <div v-if="!loading && user && user.profile" class="container-fluid">
            <div class="row">
              <div class="col-md-8 offset-md-2">
                <div class="profile-wrapper p-4 bg-white shadow-lg rounded-4">
                  <div class="d-flex justify-content-between action-buttons">
                    <div v-if="!isCurrentUserProfile"></div>

                    <router-link
                      v-if="!isCurrentUserProfile && !isOperator"
                      :to="{name: 'chat-conversation', params: {id: user.username}}"
                      class="action-button"
                    >
                      <i class="bx bxs-message-rounded"></i>
                      {{$t('message')}}
                    </router-link>

                    <div v-if="isCurrentUserProfile"></div>

                    <a
                      v-if="isCurrentUserProfile || (isOperator && user.id === currentChatUser.id)"
                      href="#"
                      class="d-flex align-items-center"
                      data-bs-toggle="modal"
                      data-bs-target="#edit-user-profile"
                    >
                      <i class="bx bx-pencil set-search"></i>
                      {{$t('edit_profile')}}
                    </a>
                  </div>

                  <UserAvatar :user="user" profile/>

                  <h2 class="text-center">
                    <span class="position-relative">
                      <i :class="{active: activeUsers.includes(user.id) || (currentChatUser && currentChatUser.id === user.id)}" class="bx bxs-circle status-icon"></i>
                      {{ user.profile.display_name }}
                    </span>
                  </h2>

                  <p class="text-center mb-2 small text-muted">
                    {{ user.profile.age }} {{$t('profile_age')}}
                    <span v-if="userLocation"> &bullet; {{ userLocation }}</span>
                  </p>

                  <p class="mb-4 fst-italic text-center">
                    "{{ user.profile.chat_status }}"
                  </p>

                  <p class="profile-bio mb-4">
                    {{ user.profile.bio_description }}
                  </p>

                  <div class="row mb-3">
                    <div class="col-md-12">
                      <h6 class="text-center mb-3 fw-bold">{{$t('profile_dating_interest')}}</h6>

                      <div class="d-flex flex-wrap justify-content-center">
                        {{user.profile.dating_interest}}
                      </div>

                      <div class="d-flex flex-wrap justify-content-center">
                        <a href="#"
                           class="btn rounded-pill text-gray me-1 mb-1"
                           v-if="isCurrentUserProfile"
                           data-bs-toggle="modal"
                           data-bs-target="#edit-user-profile"
                        >
                          <i class="bx bx-pencil me-1"></i> {{$t('update')}}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div v-if="user.hobbies.length" class="row mb-3">
                    <div class="col-md-12">
                      <h6 class="text-center mb-3 fw-bold">{{$t('profile_hobby_title')}}</h6>

                      <div class="d-flex flex-wrap justify-content-center">
                        <span
                           v-for="hobby in user.hobbies"
                           :key="hobby.hobby_key"
                           class="btn rounded-pill btn-dark me-1 mb-1 btn-hobbie cursor-default"
                        >
                          {{ hobby.hobby_key }}
                        </span>

                        <a href="#"
                           class="btn rounded-pill text-gray me-1 mb-1"
                           v-if="isCurrentUserProfile"
                           data-bs-toggle="modal"
                           data-bs-target="#edit-user-profile"
                        >
                          <i class="bx bx-plus me-1"></i> {{$t('profile_add_hobby')}}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div v-if="isCurrentUserProfile || (isOperator && isInternalUser) || userGallery.length">
                    <h6 class="text-center mb-4 fw-bold">{{$t('profile_galery_title')}}</h6>
                    <div v-if="isCurrentUserProfile || (isOperator && isInternalUser)" class="profile-cover text-center">
                      <div class="profile-cover-avatar w-auto h-auto d-flex justify-content-center">
                        <label class="btn rounded-pill text-gray m-0 p-0">
                          <i class="bx bx-plus me-1"></i> {{$t('profile_add_picture')}}
                          <input type="file" @change="addGalleryPicture($event)"/>
                        </label>
                      </div>
                    </div>

                    <masonry-wall :items="userGallery" :ssr-columns="1" :column-width="200" :gap="8">
                      <template #default="{ item, index }">
                        <div
                          :style="{ height: `${(index % 4 + 1) * 100}px` }"
                          style="cursor: pointer; position: relative;"
                          @click="show(index)"
                        >
                          <img :src="item" alt="Image" style="object-fit: cover; width: 100%; height: 100%"/>
                          <button type="button" class="btn-delete-image" @click.prevent.stop="deleteImage(item)">
                            <i class="bx bx-no-entry"></i>
                          </button>
                        </div>
                      </template>
                    </masonry-wall>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <vue-easy-lightbox
      :visible="visible"
      :index="index"
      :imgs="userGallery"
      @hide="visible = false"
    ></vue-easy-lightbox>

    <ProfileModal @saved="resetSelectedUser"/>
  </div>
</template>

<script>
import MasonryWall from '@yeger/vue-masonry-wall';
import {mapGetters} from "vuex";
import ProfileModal from "@/components/frontend/modal/ProfileModal";
import UserAvatar from "@/components/frontend/UserAvatar";
import Image from "@/components/frontend/Image";

export default {
  components: {
    Image,
    UserAvatar,
    ProfileModal,
    MasonryWall
  },

  data() {
    return {
      galleryImage: '',
      loading: true,
      user: null,
      visible: false,
      index: 0,
    }
  },

  watch: {
    $route: {
      handler() {
        if (this.isCurrentUserProfile) {
          this.loading = false;
          this.resetSelectedUser();
        } else {
          this.init();
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapGetters({
      activeUsers: 'chat/activeUsers',
      countries: 'misc/countries',
      isOperator: 'isOperator',
      currentChatUser: 'chat/currentChatUser',
      internalUsers: 'chat/internalUsers',
    }),

    isInternalUser() {
      return this.internalUsers.findIndex(e => e.id === this.user.id) >= 0;
    },

    userGallery() {
      return this.user && this.user.files ? this.user.files.filter(e => e.category === 'gallery').map(e => e.url) : [];
    },

    userLocation() {
      if (!this.user) {
        return '';
      }

      let location = '';

      if (this.user.profile.country) {
        let country = this.countries.find(e => e.id === this.user.profile.country);
        country = country ? country.text : '';

        if (!location) {
          return country;
        }

        return `${location}, ${country}`;
      }

      return '';
    },

    isCurrentUserProfile() {
      return this.$route.name === 'profile' && !this.isOperator;
    },
  },

  methods: {
    async init() {
      const r = await this.$store.dispatch('fetchUser', this.$route.params.username);
      this.user = r.data;
      this.loading = false;
    },

    async deleteImage(url) {
      let file;

      if (this.currentChatUser) {
        file = this.currentChatUser.files.find(e => e.url === url);
      } else {
        file = this.currentChatUser.files.find(e => e.url === url);
      }

      await this.$store.dispatch('deleteFile', file.id);

      if (this.currentChatUser) {
        this.resetSelectedUser();
      }
    },

    resetSelectedUser() {
      this.user = {...this.currentChatUser};
    },

    show(index) {
      this.index = index;
      this.visible = true;
    },

    async addGalleryPicture(event) {
      this.galleryImage = event.target.files[0];

      const user_id = this.currentChatUser ? this.currentChatUser.id : null;
      const r = await this.$store.dispatch('uploadImage', {
        user_id,
        category: 'gallery',
        picture: this.galleryImage,
      });

      if (r.success && this.currentChatUser) {
        this.resetSelectedUser();
      }

      this.galleryImage = '';
      event.target.value = null;
    },
  },
}
</script>
